/**
 * 구독 컨텍스트
 * 전역 구독 상태를 순수 데이터로 관리합니다.
 * 모달 상태는 포함하지 않습니다 (usePaywall 훅 사용).
 */

/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import type { ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import type { SubscriptionPlan } from '../services/types';
import * as subscriptionService from '../services/subscription.service';

/** 구독 상태 */
interface SubscriptionState {
  isPro: boolean;
  plan: SubscriptionPlan;
  isLoading: boolean;
  expirationDate: string | null;
  productId: string | null;
}

/** 구독 컨텍스트 값 타입 */
export interface SubscriptionContextValue extends SubscriptionState {
  refreshSubscription: () => Promise<void>;
}

const initialState: SubscriptionState = {
  isPro: false,
  plan: 'free',
  isLoading: true,
  expirationDate: null,
  productId: null,
};

export const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const { user } = useAuth();
  const [state, setState] = useState<SubscriptionState>(initialState);

  const updateFromInfo = useCallback((info: subscriptionService.SubscriptionInfo) => {
    setState(prev => {
      if (
        prev.isPro === info.isPro &&
        prev.plan === info.plan &&
        prev.expirationDate === info.expirationDate &&
        prev.productId === info.productId &&
        !prev.isLoading
      ) {
        return prev;
      }
      return {
        isPro: info.isPro,
        plan: info.plan,
        isLoading: false,
        expirationDate: info.expirationDate,
        productId: info.productId,
      };
    });
  }, []);

  // SDK 초기화 + 구독 상태 로드
  useEffect(() => {
    if (!user || !Capacitor.isNativePlatform()) {
      setState({ ...initialState, isLoading: false });
      return;
    }

    let mounted = true;

    async function initialize() {
      const configResult = await subscriptionService.configureRevenueCat(user!.id);
      if (!configResult.success) {
        if (mounted) setState(prev => ({ ...prev, isLoading: false }));
        return;
      }
      if (!mounted) return;

      const statusResult = await subscriptionService.getSubscriptionStatus();
      if (statusResult.success && mounted) {
        updateFromInfo(statusResult.data);
      } else if (mounted) {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    }

    initialize();

    return () => {
      mounted = false;
    };
  }, [user, updateFromInfo]);

  // CustomerInfo 리스너 등록
  useEffect(() => {
    if (!user || !Capacitor.isNativePlatform()) return;

    let mounted = true;
    let cleanup: (() => void) | null = null;

    subscriptionService.addCustomerInfoListener((info) => {
      updateFromInfo(info);
    }).then((removeListener) => {
      if (mounted) {
        cleanup = removeListener;
      } else {
        removeListener?.();
      }
    });

    return () => {
      mounted = false;
      cleanup?.();
    };
  }, [user, updateFromInfo]);

  const refreshSubscription = useCallback(async () => {
    const result = await subscriptionService.getSubscriptionStatus();
    if (result.success) {
      updateFromInfo(result.data);
    }
  }, [updateFromInfo]);

  const value = useMemo<SubscriptionContextValue>(
    () => ({
      ...state,
      refreshSubscription,
    }),
    [state, refreshSubscription]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}
